import React, { useState, useEffect } from 'react';
import {
  Card, CardHeader, CardContent, Box, Typography, Button, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
  DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  InputAdornment, Grid, Alert,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ReceiptIcon from '@mui/icons-material/Receipt';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import MobileDialog from '../../dialogs/MobileDialog';

const iconBox = (icon) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 28,
      height: 28,
      borderRadius: '50%',
      bgcolor: 'action.selected',
      color: 'text.secondary',
    }}
  >
    {React.cloneElement(icon, { fontSize: 'small' })}
  </Box>
);

const METHOD_LABELS = {
  cash: 'מזומן',
  credit_card: 'כרטיס אשראי',
  bank_transfer: 'העברה בנקאית',
  check: 'צ\'ק',
  other: 'אחר',
};

const methodIcon = (m) => {
  switch (m) {
    case 'cash': return <AttachMoneyIcon />;
    case 'credit_card': return <CreditCardIcon />;
    case 'bank_transfer': return <AccountBalanceIcon />;
    case 'check': return <ReceiptIcon />;
    case 'other': return <MoreHorizIcon />;
    default: return <PaymentIcon />;
  }
};

const formatCurrency = (n) =>
  new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' }).format(n || 0);

const formatDate = (d) => (d ? new Date(d).toLocaleDateString('he-IL') : '');

const detailsString = (p) => {
  const parts = [];
  if (p.method === 'check') {
    if (p.checkNumber) parts.push(`מס' צ'ק: ${p.checkNumber}`);
    if (p.checkBankName) parts.push(`בנק: ${p.checkBankName}`);
    if (p.checkDate) parts.push(`תאריך: ${formatDate(p.checkDate)}`);
  } else if (p.method === 'credit_card') {
    if (p.cardType) parts.push(`סוג: ${p.cardType}`);
    if (p.cardLastFour) parts.push(`מסתיים ב: ${p.cardLastFour}`);
  } else if (p.method === 'bank_transfer') {
    if (p.bankName) parts.push(`בנק: ${p.bankName}`);
    if (p.bankAccountLast4) parts.push(`חשבון: ${p.bankAccountLast4}`);
    if (p.transferDate) parts.push(`תאריך: ${formatDate(p.transferDate)}`);
  }
  return parts.join(' | ');
};

const emptyMethodFields = {
  checkNumber: '', checkDate: null, checkBankName: '', checkAccountNumber: '',
  cardLastFour: '', cardType: '',
  bankName: '', bankAccountLast4: '', transferDate: null,
};

const PaymentsSection = ({ payments, setPayments, totalAmount }) => {
  const [open, setOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('cash');
  const [fields, setFields] = useState(emptyMethodFields);
  const [error, setError] = useState('');

  const totalPaid = payments.reduce((s, p) => s + parseFloat(p.amount || 0), 0);
  const remaining = (totalAmount || 0) - totalPaid;

  useEffect(() => {
    if (!open) return;
    setError('');
    if (editingIndex !== null) {
      const p = payments[editingIndex];
      setAmount(p.amount.toString());
      setMethod(p.method);
      setFields({
        ...emptyMethodFields,
        checkNumber: p.checkNumber || '',
        checkDate: p.checkDate ? new Date(p.checkDate) : null,
        checkBankName: p.checkBankName || '',
        checkAccountNumber: p.checkAccountNumber || '',
        cardLastFour: p.cardLastFour || '',
        cardType: p.cardType || '',
        bankName: p.bankName || '',
        bankAccountLast4: p.bankAccountLast4 || '',
        transferDate: p.transferDate ? new Date(p.transferDate) : null,
      });
    } else {
      setAmount(remaining > 0 ? remaining.toFixed(2) : '');
      setMethod('cash');
      setFields(emptyMethodFields);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingIndex]);

  const openAdd = () => { setEditingIndex(null); setOpen(true); };
  const openEdit = (idx) => { setEditingIndex(idx); setOpen(true); };
  const closeDialog = () => { setOpen(false); setEditingIndex(null); };

  const handleSave = () => {
    setError('');
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) {
      setError('יש להזין סכום תשלום תקין');
      return;
    }
    const oldAmount = editingIndex !== null ? parseFloat(payments[editingIndex].amount || 0) : 0;
    const totalAfter = totalPaid - oldAmount + amt;
    if (totalAmount && totalAfter > totalAmount + 0.001) {
      const max = totalAmount - (totalPaid - oldAmount);
      setError(`סכום התשלום חורג מהסכום הכולל. ניתן להוסיף עד ${formatCurrency(max)}`);
      return;
    }

    const data = {
      amount: amt,
      method,
      notes: '',
      ...(method === 'check' && {
        checkNumber: fields.checkNumber,
        checkDate: fields.checkDate,
        checkBankName: fields.checkBankName,
        checkAccountNumber: fields.checkAccountNumber,
      }),
      ...(method === 'credit_card' && {
        cardLastFour: fields.cardLastFour,
        cardType: fields.cardType,
      }),
      ...(method === 'bank_transfer' && {
        bankName: fields.bankName,
        bankAccountLast4: fields.bankAccountLast4,
        transferDate: fields.transferDate,
      }),
    };

    if (editingIndex !== null) {
      setPayments((prev) => prev.map((p, i) => (i === editingIndex ? data : p)));
    } else {
      setPayments((prev) => [...prev, data]);
    }
    closeDialog();
  };

  const handleDelete = (idx) => {
    setPayments((prev) => prev.filter((_, i) => i !== idx));
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
              <PaymentIcon color="primary" />
              <Typography variant="h6" component="span">תשלומים</Typography>
            </Box>
            <Button
              size="small"
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              disabled={!totalAmount || remaining <= 0}
              sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}
            >
              הוסף תשלום
            </Button>
          </Box>
        }
      />
      <CardContent>
        {/* Totals strip — sums on one polished row */}
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'action.hover' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" display="block">
                סה"כ הזמנה
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(totalAmount)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" display="block">
                שולם עד כה
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {formatCurrency(totalPaid)}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography variant="caption" color="text.secondary" display="block">
                יתרה לתשלום
              </Typography>
              <Typography
                variant="h6"
                fontWeight="bold"
                color={remaining > 0.001 ? 'error.main' : 'success.main'}
              >
                {formatCurrency(remaining)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {payments.length === 0 ? (
          <Box
            sx={{
              py: 3,
              textAlign: 'center',
              border: '1px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              לא נוספו תשלומים. ההזמנה תיווצר עם יתרה פתוחה.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell>אמצעי תשלום</TableCell>
                  <TableCell>פרטים</TableCell>
                  <TableCell align="center">סכום</TableCell>
                  <TableCell align="center" sx={{ width: 100 }}>פעולות</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.map((p, idx) => (
                  <TableRow key={idx} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, direction: 'ltr' }}>
                        {iconBox(methodIcon(p.method))}
                        <Typography variant="body2" fontWeight="medium">
                          {METHOD_LABELS[p.method] || p.method}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {detailsString(p) || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" fontWeight="bold">
                        {formatCurrency(p.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="ערוך">
                        <IconButton
                          color="primary"
                          size="small"
                          onClick={() => openEdit(idx)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="הסר">
                        <IconButton
                          color="error"
                          size="small"
                          onClick={() => handleDelete(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      <MobileDialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingIndex !== null ? 'עריכת תשלום' : 'הוספת תשלום'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" type="number" label="סכום"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                onFocus={(e) => e.target.select()}
                onWheel={(e) => e.target.blur()}
                InputProps={{ endAdornment: <InputAdornment position="end">₪</InputAdornment> }}
                sx={{
                  '& input': {
                    '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                      WebkitAppearance: 'none', margin: 0,
                    },
                    MozAppearance: 'textfield',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>אמצעי תשלום</InputLabel>
                <Select value={method} label="אמצעי תשלום" onChange={(e) => setMethod(e.target.value)}>
                  {Object.entries(METHOD_LABELS).map(([k, v]) => (
                    <MenuItem key={k} value={k}>{v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {method === 'check' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="מספר צ'ק"
                    value={fields.checkNumber}
                    onChange={(e) => setFields((p) => ({ ...p, checkNumber: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="בנק (לא חובה)"
                    value={fields.checkBankName}
                    onChange={(e) => setFields((p) => ({ ...p, checkBankName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="חשבון (לא חובה)"
                    value={fields.checkAccountNumber}
                    onChange={(e) => setFields((p) => ({ ...p, checkAccountNumber: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="תאריך הצ'ק"
                    value={fields.checkDate}
                    onChange={(d) => setFields((p) => ({ ...p, checkDate: d }))}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: { fullWidth: true, size: 'small', sx: { '& .MuiInputBase-root': { direction: 'ltr' } } },
                    }}
                  />
                </Grid>
              </>
            )}

            {method === 'credit_card' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="סוג כרטיס"
                    value={fields.cardType}
                    onChange={(e) => setFields((p) => ({ ...p, cardType: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="4 ספרות אחרונות"
                    value={fields.cardLastFour}
                    onChange={(e) => setFields((p) => ({ ...p, cardLastFour: e.target.value }))}
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>
              </>
            )}

            {method === 'bank_transfer' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="בנק"
                    value={fields.bankName}
                    onChange={(e) => setFields((p) => ({ ...p, bankName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="חשבון (4 ספרות)"
                    value={fields.bankAccountLast4}
                    onChange={(e) => setFields((p) => ({ ...p, bankAccountLast4: e.target.value }))}
                    inputProps={{ maxLength: 4 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label="תאריך העברה"
                    value={fields.transferDate}
                    onChange={(d) => setFields((p) => ({ ...p, transferDate: d }))}
                    format="dd/MM/yyyy"
                    slotProps={{
                      textField: { fullWidth: true, size: 'small', sx: { '& .MuiInputBase-root': { direction: 'ltr' } } },
                    }}
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>ביטול</Button>
          <Button variant="contained" onClick={handleSave}>שמור</Button>
        </DialogActions>
      </MobileDialog>
    </Card>
  );
};

export default PaymentsSection;
